/* global Swiper */

/**
 * TrustPilotReviews custom element
 * @extends HTMLElement
 */
class TrustPilotReviews extends HTMLElement {
	constructor() {
		super();
		/** @type {MutationObserver | null} */
		this.observer = null;
		/** @type {any} */
		this.swiperInstance = null;
		this.reviewsFetched = false;
	}

	connectedCallback() {
		this.initYotpoObserver();
	}

	initYotpoObserver() {
		// @ts-ignore
		const yotpoWidget = document.querySelector(`.yotpo-widget-instance[data-yotpo-instance-id="${window.Shopify.yotpo.topPdpInstanceId}"]`);

		if (yotpoWidget) {
			this.observeWidget(yotpoWidget);
		} else {
			const bodyObserver = new MutationObserver((mutations, observer) => {
				// @ts-ignore
				const widget = document.querySelector(`.yotpo-widget-instance[data-yotpo-instance-id="${window.Shopify.yotpo.topPdpInstanceId}"]`);
				if (widget) {
					this.observeWidget(widget);
					observer.disconnect();
				}
			});

			bodyObserver.observe(document.body, {
				childList: true,
				subtree: true
			});
			this.observer = bodyObserver;
		}
	}

	/**
	 * @param {Element} widget 
	 */
	observeWidget(widget) {
		this.checkRating(widget);
		const observer = new MutationObserver(() => {
			this.checkRating(widget);
		});

		observer.observe(widget, {
			childList: true,
			subtree: true
		});
		this.observer = observer;
	}

	/**
	 * @param {Element} widget 
	 */
	checkRating(widget) {
		const ratingElement = widget.querySelector('.yotpo-sr-bottom-line-text.yotpo-sr-bottom-line-text--right-panel');

		if (ratingElement) {
			const ratingValue = (/** @type {HTMLElement} */ (ratingElement)).innerText.trim();
			const parts = ratingValue.split('Reviews');
			const rating = parts[0] ? parts[0].trim() : '0';
			const numericValue = parseFloat(rating);
			this.handleRating(Number.isFinite(numericValue) ? numericValue : 0);
		}
	}

	/**
	 * @param {number | string} value 
	 */
	handleRating(value) {
		const numericRating = parseFloat(String(value));
		const thresholdAttr = this.getAttribute('data-target');
		const threshold = thresholdAttr ? parseFloat(thresholdAttr) : 5;

		if (!isNaN(numericRating)) {
			if (numericRating < threshold) {
				if (!this.reviewsFetched) {
					this.fetchTrustPilotReviews();
				}
			} else {
				this.style.display = 'none';
				this.classList.add('hidden');
			}
		}
	}

	async fetchTrustPilotReviews() {
		this.reviewsFetched = true;
		try {
			const response = await fetch('/apps/reviews/v1/trustpilot/reviews?perPage=15');
			const data = await response.json();
			if (data.success && data.data && data.data.results) {
				this.renderReviews(data.data.results);
			}
		} catch (error) {
			console.error('Error fetching Trustpilot reviews:', error);
		}
	}

	/**
	 * @param {any[]} reviews 
	 */
	renderReviews(reviews) {
		const container = this.querySelector('.js-trustpilot-container');
		if (!container) return;

		const sectionId = this.getAttribute('data-section-id') || '';
		const verifiedUrl = this.getAttribute('data-verified-url') || '';

		const svgFull = document.getElementById(`tp-svg-full-${sectionId}`)?.innerHTML || '';
		const svgEmpty = document.getElementById(`tp-svg-empty-${sectionId}`)?.innerHTML || '';
		const svgVerified = document.getElementById(`tp-svg-verified-${sectionId}`)?.innerHTML || '';

		if (!svgFull || !svgEmpty) {
			console.warn('Trustpilot SVGs not found');
		}

		container.innerHTML = reviews.map((/** @type {any} */ r) => {
			const starsHtml = Array.from({ length: 5 }, (_, i) =>
				(i < r.stars) ? svgFull : svgEmpty
			).join('');

			const verifiedHtml = r.isVerified ? `
                <span class="tp-card__verified">
                    <a href="${verifiedUrl}">
                        ${svgVerified}
                        Verified
                    </a>
                </span>
            ` : '';

			const timeAgoText = this.diffForHumans(r.createdAt);

			return `
                <div class="swiper-slide">
                    <div class="tp-card">
                        <div class="tp-card__header">
                            <div class="tp-card__stars" aria-label="Rated ${r.stars} out of 5">
                                ${starsHtml}
                            </div>
                            ${verifiedHtml}
                        </div>
						<div class="tp-card__body">
						    <a href="https://www.trustpilot.com/reviews/${r.id}?utm_medium=trustbox&utm_source=Carousel" target="_blank" class="review-link">
								<div class="tp-card__title">${this.escapeHtml(r.title)}</div>
								<div class="tp-card__body">${this.escapeHtml(r.text)}</div>
								<div class="tp-card__author">
									<strong>${this.escapeHtml(r.consumer.displayName)},</strong>
									${timeAgoText}
								</div>
							</a>
						</div>
                    </div>
                </div>
            `;
		}).join('');

		this.initSwiper();
		this.classList.remove('hidden');
		this.style.display = 'block';
	}

	initSwiper() {
		const sectionId = this.getAttribute('data-section-id');
		if (!sectionId) return;

		const el = this.querySelector(`.tp-swiper-${sectionId}`);
		const prev = this.querySelector(`.tp-prev-${sectionId}`);
		const next = this.querySelector(`.tp-next-${sectionId}`);

		// Use window.Swiper to satisfy lint and handle global reference
		const SwiperRef = (/** @type {any} */ (window)).Swiper;

		if (!el || typeof SwiperRef === 'undefined') {
			if (typeof SwiperRef === 'undefined') {
				setTimeout(() => this.initSwiper(), 200);
			}
			return;
		}

		if (this.swiperInstance) {
			this.swiperInstance.destroy();
		}

		const slidesMobile = this.getAttribute('data-slides-mobile');
		const slidesTablet = this.getAttribute('data-slides-tablet');
		const slidesIpad = this.getAttribute('data-slides-ipad');
		const slidesDesktop = this.getAttribute('data-slides-desktop');

		this.swiperInstance = new SwiperRef(el, {
			slidesPerView: slidesMobile ? parseInt(slidesMobile) : 1,
			spaceBetween: 0,
			slidesPerGroup: 1,
			navigation: {
				prevEl: prev,
				nextEl: next,
				disabledClass: 'tp-disabled'
			},
			breakpoints: {
				767: { slidesPerView: slidesTablet ? parseInt(slidesTablet) : 2, slidesPerGroup:slidesTablet },
				1024: { slidesPerView: slidesIpad ? parseInt(slidesIpad) : 3, slidesPerGroup:slidesIpad },
				1200: { slidesPerView: slidesDesktop ? parseInt(slidesDesktop) : 4, slidesPerGroup:slidesDesktop }
			}
		});
	}

	/**
	 * @param {string | number | Date} dateInput 
	 */
	diffForHumans(dateInput) {
		const now = new Date();
		const date = new Date(dateInput);

		const diff = now.getTime() - date.getTime();
		const seconds = Math.floor(diff / 1000);
		const future = seconds < 0;
		const absSeconds = Math.abs(seconds);

		const days = Math.floor(absSeconds / 86400);

		if (days === 0 && absSeconds < 60) return "just now";
		if (days === 1) return future ? "Tomorrow" : "Yesterday";

		const intervals = [
			{ label: 'year', value: 31536000 },
			{ label: 'month', value: 2592000 },
			{ label: 'day', value: 86400 },
			{ label: 'hour', value: 3600 },
			{ label: 'minute', value: 60 }
		];

		for (const i of intervals) {
			const count = Math.floor(absSeconds / i.value);
			if (count >= 1) {
				return future
					? `in ${count} ${i.label}${count > 1 ? 's' : ''}`
					: `${count} ${i.label}${count > 1 ? 's' : ''} ago`;
			}
		}
		return 'just now';
	}

	/**
	 * @param {string} unsafe 
	 */
	escapeHtml(unsafe) {
		return String(unsafe)
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#039;");
	}

	disconnectedCallback() {
		if (this.observer) {
			this.observer.disconnect();
		}
		if (this.swiperInstance) {
			this.swiperInstance.destroy();
		}
	}
}

if (!customElements.get('trustpilot-reviews')) {
	customElements.define('trustpilot-reviews', TrustPilotReviews);
}

